import { useQuery } from '@tanstack/react-query';
import { useAuthedFetch, parseJsonOrThrow } from './client';
import { HistoryResponse } from '../domain';

export type HistoryEntityKind = 'floor' | 'group' | 'heat_pump_link' | 'controller' | 'weather';

export interface HistoryRange {
  from?: string;
  to?: string;
  limit?: number;
  // When set, the API downsamples server-side into this many time buckets.
  maxPoints?: number;
}

// How often to re-poll a history range. Short raw ranges are cheap and want to
// feel live. A bucketed range is not: a 30-day request scans up to ~21,600 rows
// across ~22 sequential Table Storage pages (~1–2 s), and the heat-pump page
// holds up to four such keys at once — so polling it every minute spends a
// large slice of every minute on queries whose underlying data moves by one
// bucket every hour. Back those off to 5 minutes.
// These are also the quantum `useChartRange` floors `from` to, so the query key
// turns over in step with the poll rather than faster than it.
export const RAW_POLL_MS = 60_000;
export const BUCKETED_POLL_MS = 5 * 60_000;

export function historyPollMs(range: HistoryRange): number {
  return range.maxPoints ? BUCKETED_POLL_MS : RAW_POLL_MS;
}

export function useHistoryQuery(
  kind: HistoryEntityKind,
  name: string,
  range: HistoryRange = {},
) {
  const fetcher = useAuthedFetch();
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  if (range.limit) params.set('limit', String(range.limit));
  if (range.maxPoints) params.set('maxPoints', String(range.maxPoints));
  const qs = params.toString();

  return useQuery({
    queryKey: ['history', kind, name, range.from ?? null, range.to ?? null, range.limit ?? null, range.maxPoints ?? null],
    queryFn: async () => {
      const res = await fetcher(`/history/${kind}/${name}${qs ? `?${qs}` : ''}`);
      return parseJsonOrThrow(res, `GET /history/${kind}/${name}`, (raw) => HistoryResponse.parse(raw));
    },
    // `range.from` is re-floored to this range's poll cadence (see
    // useChartRange), so each poll — and every range switch — produces a
    // NEW query key. A new key is a cache MISS, not a refetch: without this,
    // `data` would go `undefined` and `isLoading` `true` while the fetch runs,
    // blanking every chart on the page once a minute.
    //
    // Narrowed to the SAME entity: `/floors/:floor` is not keyed in main.tsx,
    // so top → middle reuses this hook instance, and an unconditional
    // keepPreviousData would paint the previous floor's readings under the new
    // floor's heading. Wrong data is worse than no data.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[1] === kind && prevQuery?.queryKey[2] === name ? prev : undefined,
    refetchInterval: historyPollMs(range),
    // Match staleTime to the poll so a remount inside the same window reuses
    // the cached result instead of firing another expensive scan.
    staleTime: historyPollMs(range),
  });
}
